
import { defineComponent, onMounted, ref, computed } from "vue";
import { useRoute } from "vue-router";
import Swal from "sweetalert2/dist/sweetalert2.min.js";

import ApiService from "@/core/services/ApiService";
import AddProductModal from "@/components/modals/general/AddProductModal.vue";
import EditProductModal from "@/components/modals/general/EditProductModal.vue";

interface Product {
  product_name: string;
  product_category: string;
  product_url: string;
  product_id: string;
  product_price: string;
  product_description: string;
  product_sku: string;
  product_status: string;
  fulfill_type: string;
  purchase_type: string;
  shipping_carrier: string;
  tax_class: string;
  shipping_tax: string;
  physical_product: string;
}

export default defineComponent({
  name: "merchant-products",
  components: {
    AddProductModal,
    EditProductModal,
  },
  setup() {
    onMounted(() => {
      setTimeout(async () => {
        await getProducts();
      }, 1000);
    });

    const route = useRoute();
    const loading = ref(false);
    const products = ref<Product[]>([]);
    const selectedIndex = ref(0);

    const getMerchantId = computed(() => {
      return route.params.id;
    });

    const getProducts = async () => {
      loading.value = true;

      try {
        const url = `merchants/accounts/${getMerchantId.value}?fields=merchantProduct`;

        const { data } = await ApiService.get(url);
        products.value = JSON.parse(data.data[0].merchantProduct);
      } catch (err) {
        products.value = [];
      }

      loading.value = false;
    };

    const onNewProductSubmit = () => {
      getProducts();
    };

    const onRemove = async (index) => {
      // eslint-disable-next-line
      const payload: any = Object.assign([], products.value);
      payload.splice(index, 1);

      loading.value = true;

      try {
        const res = await ApiService.put(
          `merchants/accounts/${getMerchantId.value}?merchantProduct`,
          payload
        );
        if (res.data.success) {
          Swal.fire({
            text: "You have successfully removed the product!",
            icon: "success",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-primary",
            },
          }).then(function () {
            getProducts();
          });
        } else {
          Swal.fire({
            text: res.data.error.message,
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Try again!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-danger",
            },
          });
        }
      } catch (e) {
        Swal.fire({
          text: e.response.data.error
            ? e.response.data.error.message
            : "Something went wrong!",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Try again!",
          customClass: {
            confirmButton: "btn fw-bold btn-light-danger",
          },
        });
      }

      loading.value = false;
    };

    const onEdit = (val) => {
      selectedIndex.value = val;
    };

    const getIsDataEmpty = computed(() => {
      if (products.value.length === 0) {
        return true;
      }

      return false;
    });

    return {
      loading,
      products,
      onNewProductSubmit,
      onRemove,
      onEdit,
      selectedIndex,
      getIsDataEmpty,
    };
  },
});
